import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './BottomSelectionPanel.css';
import type { ActionItem, SelectionSummary } from './ActionsMenuBar';

interface Props {
  isOpen: boolean;
  actions: ActionItem[];
  onClose: () => void;
  onCancel?: () => void;
  selectionSummary?: SelectionSummary;
  formatAmount?: (amount?: number) => string;
  formatHours?: (hours?: number) => string;
  className?: string;
}

const ANIMATION_MS = 140;

const BottomSelectionPanel: React.FC<Props> = React.memo(({
  isOpen,
  actions,
  onClose,
  onCancel,
  selectionSummary,
  formatAmount,
  formatHours,
  className = ''
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  // Ensure bottom root exists
  useEffect(() => {
    const id = 'bottom-elements-root';
    let el = document.getElementById(id) as HTMLDivElement | null;
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      document.body.appendChild(el);
    }
    rootRef.current = el;
    return () => {
      // keep it mounted for other components
      rootRef.current = null;
    };
  }, []);
 
  // manage root classes to enable pointer-events and global animations
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (isOpen) {
      root.classList.add('bottom-elements-open');
      root.classList.remove('bottom-elements-closing');
    } else {
      root.classList.remove('bottom-elements-open');
      root.classList.remove('bottom-elements-closing');
    }
    return () => {
      // leave root in neutral state; specific close animation handled below
      root.classList.remove('bottom-elements-open');
      root.classList.remove('bottom-elements-closing');
    };
  }, [isOpen]);

  // Measure and set CSS variable for total height to avoid layout thrash
  const measure = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const height = Math.max(0, Math.round(rect.height));
    document.documentElement.style.setProperty('--bottom-elements-height', `${height}px`);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // ensure CSS var reset
      document.documentElement.style.setProperty('--bottom-elements-height', '0px');
      return;
    }
    measure();
    let raf = 0;
    raf = requestAnimationFrame(() => measure());
    const ro = new ResizeObserver(() => {
      // batch measurements to next frame
      requestAnimationFrame(measure);
    });
    if (panelRef.current) ro.observe(panelRef.current);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [isOpen, measure]);

  const startCloseAnimation = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsClosing(true);
    const root = rootRef.current;
    if (root) {
      root.classList.remove('bottom-elements-open');
      root.classList.add('bottom-elements-closing');
    }
    closeTimerRef.current = window.setTimeout(() => {
      setIsClosing(false);
      if (root) {
        root.classList.remove('bottom-elements-closing');
      }
      onClose();
      closeTimerRef.current = null;
    }, ANIMATION_MS);
  }, [onClose]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  const handleActionClick = useCallback((action: ActionItem) => {
    // Execute action immediately for instant response (open modal etc.)
    try {
      const res = (action.onClick as any)();
      if (res && typeof res.then === 'function') {
        (res as Promise<any>).catch(() => {});
      }
    } catch (err) {
      // ignore
    }
    // Then start hide animation so UI stays snappy
    startCloseAnimation();
  }, [startCloseAnimation]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    startCloseAnimation();
  }, [onCancel, startCloseAnimation]);

  if (!isOpen || !rootRef.current) return null;

  const container = rootRef.current;

  return createPortal(
    <div
      ref={panelRef}
      className={`bottom-selection-panel ${isClosing ? 'closing' : 'open'} ${className}`}
      role="dialog"
      aria-hidden={!isOpen}
    >
      {/* Information block on top */}
      <div className="bottom-selection-panel-row info" aria-hidden={!selectionSummary}>
        {selectionSummary && (
          <div className="bsp-info">
            <div className="bsp-info-left">
              <div className="bsp-info-item">
                <div className="label">Часы</div>
                <div className="value">{formatHours ? formatHours(selectionSummary.totalHours) : (selectionSummary.totalHours ?? '—')}</div>
              </div>
              {selectionSummary.hasFinancialAccess && (
                <>
                  <div className="bsp-info-item">
                    <div className="label">Наработано</div>
                    <div className="value">{formatAmount ? formatAmount(selectionSummary.totalAmount) : (selectionSummary.totalAmount ?? '—')}</div>
                  </div>
                  <div className="bsp-info-item">
                    <div className="label">Выплачено</div>
                    <div className="value">{formatAmount ? formatAmount(selectionSummary.paidAmount) : (selectionSummary.paidAmount ?? '—')}</div>
                  </div>
                  {(() => {
                    const unpaidBonus = selectionSummary.unpaidBonusAmount ?? 0;
                    const normalizedUnpaidBonus = Math.round(unpaidBonus * 100) / 100;
                    if (normalizedUnpaidBonus > 0.01) {
                      return (
                        <div className="bsp-info-item remaining">
                          <div className="label">Неопл. премия</div>
                          <div className="value">{formatAmount ? formatAmount(unpaidBonus) : unpaidBonus}</div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {(() => {
                    const remaining = selectionSummary.remainingAmount ?? 0;
                    const normalizedRemaining = Math.round(remaining * 100) / 100;
                    if (normalizedRemaining > 0.01) {
                      return (
                        <div className="bsp-info-item remaining">
                          <div className="label">Остаток</div>
                          <div className="value">{formatAmount ? formatAmount(remaining) : remaining}</div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
            </div>

            {/* placeholder for alignment; advances moved to separate row */}
            <div className="bsp-info-right" />
          </div>
        )}
      </div>
      {/* Separate row for unallocated advances */}
      <div className={`bottom-selection-panel-row advances ${selectionSummary && (selectionSummary.unallocatedAdvancesSum ?? 0) > 0 ? 'has-advances' : ''}`} aria-hidden={selectionSummary == null || selectionSummary.selectedUserId == null}>
        {selectionSummary && selectionSummary.selectedUserId != null && (
          <>
            {selectionSummary.unallocatedAdvancesLoading ? (
              <div className="bsp-advances-loading">
                <i className="ri-loader-4-line ri-spin" /> <span>Загрузка авансов...</span>
              </div>
            ) : selectionSummary.unallocatedAdvancesSum != null ? (
              <div className="bsp-advances-sum-row">
                <div className="label">Нераспределённые авансы</div>
                <div className="value">{formatAmount ? formatAmount(selectionSummary.unallocatedAdvancesSum) : (selectionSummary.unallocatedAdvancesSum ?? '—')}</div>
              </div>
            ) : (
              <div className="bsp-advances-sum-row bsp-advances-unavailable">
                <div className="label">Нераспределённые авансы</div>
                <div className="value">—</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions row at bottom with cancel as first item and horizontal scroll for buttons */}
      <div className="bottom-selection-panel-row actions">
        {onCancel && (
          <button
            type="button"
            className="bsp-cancel"
            onClick={handleCancel}
            aria-label="Отменить выделение"
          >
            <i className="ri-close-line" />
          </button>
        )}
        <div className="actions-scroll" aria-hidden={actions.length === 0}>
          {actions.map(a => (
            <button
              key={a.id}
              className={`bsp-action bsp-action--${a.variant || 'primary'} ${a.disabled ? 'disabled' : ''}`}
              onClick={() => !a.disabled && handleActionClick(a)}
              disabled={a.disabled}
              title={a.label}
            >
              {a.icon && <i className={a.icon} />}
              <span>{a.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>,
    container
  );
});

BottomSelectionPanel.displayName = 'BottomSelectionPanel';

export default BottomSelectionPanel;

